import React, { useState, useEffect, useRef, useCallback, useMemo, useReducer } from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { message } from 'antd';
import agentActions from '../../../../actions/agent';
import { uploadImage } from '../../../../actions/imageUpload';
import { spacing, typography } from '../../../../styles/theme';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import AssetPickerModal from '../../../../components/AssetPickerModal';
import ThreadInfoModal from './ThreadInfoModal';
import { VG } from '../agentTheme';
import useAnalysisPolling from '../hooks/useAnalysisPolling';
import useStreamStallDetection from '../hooks/useStreamStallDetection';
import PresetPromptsModal from './PresetPromptsModal';
import { AnalysisResultCard } from './smartMessages';

export default function ConversationChat({
  threadId,
  selectedBrandId,
  selectedBrand,
  navigate,
  hasNoBrands,
  isMobile,
  onOpenSidebar,
  completedImages,
  onNewJobIds,
  onThreadLoaded,
  useAsInputRef,
}) {
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const pendingMessageRef = useRef(null);
  const pendingImagesRef = useRef([]);
  const lastSentImagesRef = useRef([]);
  const reportedJobIdsRef = useRef(new Set());

  const [activeThread, setActiveThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [attachedImages, setAttachedImages] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [messageImages, setMessageImages] = useState({});
  const [devMode, setDevMode] = useState(() => localStorage.getItem('agent_dev_mode') === '1');
  const [threadInfoOpen, setThreadInfoOpen] = useState(false);
  const [chatNetworkError, setChatNetworkError] = useState(null);
  const [queuedMessage, setQueuedMessage] = useState(null);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const wasInterruptedRef = useRef(false);

  // ─── Single AssetPickerModal instance (shared by ChatInput + AssetVerifyCard) ─
  const [assetPicker, dispatchAssetPicker] = useReducer(
    (state, action) => {
      if (action.type === 'OPEN') return { open: true, onConfirm: action.onConfirm };
      if (action.type === 'CLOSE') return { open: false, onConfirm: null };
      return state;
    },
    { open: false, onConfirm: null }
  );

  const openAssetPicker = useCallback((onConfirm) => {
    dispatchAssetPicker({ type: 'OPEN', onConfirm });
  }, []);

  const closeAssetPicker = useCallback(() => {
    dispatchAssetPicker({ type: 'CLOSE' });
  }, []);

  const toggleDevMode = () => setDevMode(prev => {
    const next = !prev;
    localStorage.setItem('agent_dev_mode', next ? '1' : '0');
    return next;
  });

  const transport = useMemo(() => {
    if (!threadId) return undefined;
    return new DefaultChatTransport({
      api: agentActions.getStreamUrl(),
      headers: () => ({ Authorization: `Bearer ${agentActions.getAuthToken()}` }),
      prepareSendMessagesRequest: ({ messages: msgs }) => {
        const lastMsg = msgs[msgs.length - 1];
        const text = typeof lastMsg?.content === 'string'
          ? lastMsg.content
          : lastMsg?.parts?.filter(p => p.type === 'text').map(p => p.text).join('') || '';
        const body = { message: text, threadId };
        if (pendingImagesRef.current.length > 0) {
          body.images = pendingImagesRef.current;
          pendingImagesRef.current = [];
        }
        return { body };
      },
    });
  }, [threadId]);

  const { messages, status, setMessages, sendMessage, stop: stopChat, error: chatError } = useChat({
    transport,
    id: threadId || 'new',
  });

  const isStreaming = status === 'streaming' || status === 'submitted';

  // ─── Custom hooks ────────────────────────────────────────────────────────
  const { analysisResults, setAnalysisResults } = useAnalysisPolling(threadId, reportedJobIdsRef, messages.length);
  useStreamStallDetection(isStreaming, messages.length, stopChat, setChatNetworkError);

  // ─── Effects ──────────────────────────────────────────────────────────────

  // Send pending message after auto-thread creation + navigation
  useEffect(() => {
    if (pendingMessageRef.current && threadId && status === 'ready') {
      const text = pendingMessageRef.current;
      const timer = setTimeout(() => { pendingMessageRef.current = null; sendMessage({ text }); }, 50);
      return () => clearTimeout(timer);
    }
  }, [threadId, status, sendMessage]);

  // Auto-send queued message when agent finishes
  useEffect(() => {
    if (queuedMessage && status === 'ready' && threadId) {
      const text = queuedMessage;
      setQueuedMessage(null);
      setInputText('');
      const timer = setTimeout(() => sendMessage({ text }), 100);
      return () => clearTimeout(timer);
    }
  }, [queuedMessage, status, threadId, sendMessage]);

  useEffect(() => {
    if (chatError) {
      const msg = chatError?.message || 'Failed to send message';
      const isNetworkError = msg.toLowerCase().includes('network') || msg.toLowerCase().includes('fetch') || msg.toLowerCase().includes('connect') || msg.toLowerCase().includes('refused');
      if (isNetworkError) {
        setChatNetworkError('Lost connection to the AI server. Check that the server is running.');
      } else {
        setChatNetworkError(msg);
      }
    } else {
      setChatNetworkError(null);
    }
  }, [chatError]);

  const handleScrollContainer = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrolledUp = (el.scrollHeight - el.scrollTop - el.clientHeight) > 80;
    userScrolledUpRef.current = scrolledUp;
    setShowScrollButton(scrolledUp);
  }, []);

  useEffect(() => {
    if (userScrolledUpRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset reported job IDs when thread changes
  useEffect(() => {
    reportedJobIdsRef.current = new Set();
  }, [threadId]);

  // Load thread messages when URL threadId changes
  useEffect(() => {
    if (!threadId) { setActiveThread(null); setMessages([]); setThreadLoading(false); return; }
    if (pendingMessageRef.current) return;

    setThreadLoading(true);
    agentActions.getThread(threadId)
      .then(res => {
        if (!res?.success) return;
        const thread = res.data.thread;
        setActiveThread(thread);
        onThreadLoaded?.(thread);

        const chatMessages = (res.data.messages || []).map(m => {
          const rawContent = m.content;
          let textContent = '';
          let imageUrls = [];
          let parts = [];

          if (rawContent && typeof rawContent === 'object' && rawContent.format === 2) {
            parts = (rawContent.parts || []).map(p => {
              if (p.type === 'tool-invocation' && p.toolInvocation) {
                const ti = p.toolInvocation;
                return {
                  type: `tool-${ti.toolName}`, toolCallId: ti.toolCallId,
                  state: ti.state === 'result' ? 'output-available' : 'input-available',
                  input: ti.args, output: ti.result,
                };
              }
              if (p.type === 'image') {
                const imgUrl = typeof p.image === 'string' ? p.image : (p.url || p.data);
                if (imgUrl) imageUrls.push(imgUrl);
              }
              if (p.type === 'file' && p.mimeType?.startsWith('image/')) {
                imageUrls.push(p.url || p.data);
              }
              return p;
            });
            textContent = (rawContent.parts || [])
              .filter(p => p.type === 'text' && (devMode || !p.text?.match(/^\[Image \d+:/)))
              .map(p => p.text).join('');
          } else if (Array.isArray(rawContent)) {
            textContent = rawContent.filter(p => p.type === 'text').map(p => p.text).join('');
            imageUrls = rawContent.filter(p => p.type === 'image').map(p => p.image || p.url).filter(Boolean);
          } else {
            textContent = typeof rawContent === 'string' ? rawContent
              : rawContent?.content || rawContent?.parts?.[0]?.text || '';
          }

          return { id: m.id, role: m.role, content: textContent, parts, imageUrls };
        });
        setMessages(chatMessages);
        setThreadLoading(false);
      })
      .catch(err => {
        console.error('Failed to load thread:', err);
        setThreadLoading(false);
        message.error('Thread not found');
        navigate('/agent');
      });
  // Deps: setMessages/navigate are stable, devMode intentionally excluded (don't reload thread on toggle)
  }, [threadId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Report new job IDs found in messages to AssetPanel (via entry callback)
  useEffect(() => {
    const newIds = [];
    for (const msg of messages) {
      for (const part of msg.parts || []) {
        if (part.type?.startsWith('tool-') && part.toolCallId && part.output) {
          const id = part.output?.image_job_id || part.output?.workflow_job_id
            || part.output?.data?.image_job_id || part.output?.data?.workflow_job_id;
          if (id && !reportedJobIdsRef.current.has(id)) {
            newIds.push(id);
            reportedJobIdsRef.current.add(id);
          }
        }
        if (part.type === 'data-generation-started') {
          const id = part.data?.image_job_id || part.data?.workflow_job_id;
          if (id && !reportedJobIdsRef.current.has(id)) {
            newIds.push(id);
            reportedJobIdsRef.current.add(id);
          }
        }
      }
    }
    if (newIds.length > 0) onNewJobIds?.(newIds);
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Associate sent images with their user message — fires on any message ID change
  const lastMsgId = messages[messages.length - 1]?.id;
  useEffect(() => {
    if (lastSentImagesRef.current.length === 0 || messages.length === 0) return;
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    if (lastUserMsg?.id) {
      const urls = [...lastSentImagesRef.current];
      lastSentImagesRef.current = [];
      setMessageImages(prev => ({ ...prev, [lastUserMsg.id]: urls }));
    }
  }, [lastMsgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleSend = useCallback(async (text) => {
    if (!text?.trim() && attachedImages.length === 0) return;
    if (isStreaming) return;

    const trimmed = (text || '').trim();
    setChatNetworkError(null);
    setInputText('');
    userScrolledUpRef.current = false;

    if (attachedImages.length > 0) {
      const imagesToUpload = [...attachedImages];
      setAttachedImages([]);
      const needsUpload = imagesToUpload.some(img => img.file);
      if (needsUpload) setUploadingCount(imagesToUpload.filter(img => img.file).length);
      let failedUploads = 0;
      const uploadedImages = await Promise.all(
        imagesToUpload.map(async (img) => {
          const preview = img.url;
          let url = preview;
          if (img.file) {
            try {
              const res = await uploadImage(img.file);
              if (res?.image_url) url = res.image_url;
              else failedUploads++;
            } catch (_) { failedUploads++; }
          }
          return { url, preview, name: img.name || null, role: img.role || null };
        })
      );
      setUploadingCount(0);
      if (failedUploads > 0) {
        message.warning(`${failedUploads} image${failedUploads > 1 ? 's' : ''} failed to upload — the AI can see ${failedUploads > 1 ? 'them' : 'it'} but won't be able to pass ${failedUploads > 1 ? 'them' : 'it'} to image generation.`);
      }
      lastSentImagesRef.current = uploadedImages.map(i => i.url);
      pendingImagesRef.current = uploadedImages;
    }

    if (!threadId) {
      if (!selectedBrandId) { message.warning('Please select a brand first'); return; }
      try {
        const res = await agentActions.createThread({ brand_id: selectedBrandId, brand_name: selectedBrand?.brand_name });
        if (res?.success) {
          pendingMessageRef.current = trimmed;
          navigate(`/agent/${res.data.id}`);
          return;
        }
      } catch (err) {
        message.error(err?.error || 'Failed to create thread');
        lastSentImagesRef.current = [];
        pendingImagesRef.current = [];
        return;
      }
    }

    // wasInterruptedRef is cleared — interrupted context is now injected server-side via thread metadata
    wasInterruptedRef.current = false;

    sendMessage({ text: trimmed });
  }, [threadId, selectedBrandId, selectedBrand, navigate, sendMessage, isStreaming, attachedImages]);

  const handleUseAsInput = useCallback((url) => {
    setAttachedImages(prev => [...prev, { url, name: 'image' }]);
    message.success('Added to input');
  }, []);

  // Expose handleUseAsInput to parent (for cross-panel "use as input" from AssetPanel)
  useEffect(() => {
    if (useAsInputRef) useAsInputRef.current = handleUseAsInput;
  }, [handleUseAsInput, useAsInputRef]);

  const handleImageAction = useCallback((url, action) => {
    if (isStreaming) return;
    const imageObj = { url, preview: url, name: 'generated-image', role: 'product' };
    if (action === 'use-as-input') { setAttachedImages(prev => [...prev, imageObj]); return; }
    const actionText = action === 'upscale' ? 'Upscale this image to 4K'
      : action === 'remove-bg' ? 'Remove the background from this image' : null;
    if (!actionText) return;
    pendingImagesRef.current = [imageObj];
    lastSentImagesRef.current = [url];
    sendMessage({ text: actionText });
  }, [isStreaming, sendMessage]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, position: 'relative', zIndex: 1 }}>
      {/* Header — hidden on mobile (MobileHeader handles it) */}
      {!isMobile && <div style={{
        display: 'flex', alignItems: 'center', gap: spacing.sm,
        padding: `${spacing.sm}px ${spacing.lg}px`,
        borderBottom: '1px solid rgba(255,255,255,0.08)', minHeight: 52,
        background: 'rgba(255,255,255,0.025)',
        flexShrink: 0, position: 'relative', zIndex: 1,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            fontSize: typography.fontSize.md, fontWeight: 600,
            color: VG.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
            letterSpacing: '-0.01em',
          }}>
            {activeThread?.title || 'AI Agent'}
          </span>
          {selectedBrand && (
            <span style={{ fontSize: typography.fontSize.xs, color: VG.textMuted, display: 'block', marginTop: 1 }}>
              {selectedBrand.brand_name}
            </span>
          )}
        </div>
        {isStreaming && (
          <span style={{
            fontSize: 11, fontWeight: 500, color: VG.info,
            padding: '3px 12px', background: 'rgba(56,189,248,0.08)',
            borderRadius: 20, border: '1px solid rgba(56,189,248,0.2)',
            animation: 'pulse 2s ease-in-out infinite',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: VG.info, animation: 'pulse 1.5s ease-in-out infinite' }} />
            thinking
          </span>
        )}
        {/* Dev tools — INFO only visible when dev mode is on */}
        {devMode && threadId && (
          <button
            onClick={() => setThreadInfoOpen(true)}
            title="Thread debug info"
            style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
              padding: '3px 8px', borderRadius: 6, cursor: 'pointer', flexShrink: 0,
              border: '1px solid rgba(255, 105, 135, 0.3)',
              background: 'rgba(255, 105, 135, 0.06)',
              color: 'rgba(255, 105, 135, 0.7)',
              transition: 'all 150ms ease',
            }}
          >INFO</button>
        )}
        <button
          onClick={toggleDevMode}
          aria-label={devMode ? 'Disable dev mode' : 'Enable dev mode'}
          title={devMode ? 'Dev mode ON' : 'Dev mode OFF'}
          style={{
            width: 28, height: 28, borderRadius: 6, cursor: 'pointer', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: devMode ? '1px solid rgba(251,191,36,0.5)' : '1px solid rgba(255,255,255,0.06)',
            background: devMode ? 'rgba(251,191,36,0.1)' : 'transparent',
            color: devMode ? '#fbbf24' : 'rgba(255,255,255,0.15)',
            transition: 'all 150ms ease',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
          </svg>
        </button>
      </div>}

      {/* Messages area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScrollContainer}
        style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px 16px' : `${spacing.lg}px ${spacing.xl}px`, display: 'flex', flexDirection: 'column' }}
      >
        {/* Thread loading skeleton */}
        {threadLoading && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, padding: '20px 0' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: i % 2 === 0 ? 'flex-end' : 'flex-start', flexDirection: i % 2 === 0 ? 'row-reverse' : 'row' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent)', animation: 'shimmer 1.5s ease infinite' }} />
                </div>
                <div style={{ width: i === 2 ? '45%' : '65%', maxWidth: 400, borderRadius: 14, overflow: 'hidden', position: 'relative' }}>
                  <div style={{ height: i === 1 ? 60 : i === 2 ? 40 : 80, background: 'rgba(255,255,255,0.03)' }}>
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent)', animation: 'shimmer 1.5s ease infinite' }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No brands state */}
        {!threadLoading && hasNoBrands && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: spacing.lg, textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: 'linear-gradient(135deg, rgba(255,126,103,0.15), rgba(255,77,141,0.15))',
              border: '1px solid rgba(255,126,103,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: spacing.sm,
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FF7E67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
              </svg>
            </div>
            <h2 style={{ fontSize: typography.fontSize.xl, fontWeight: 700, color: VG.textPrimary, margin: 0, letterSpacing: '-0.02em' }}>Create a Brand First</h2>
            <p style={{ fontSize: typography.fontSize.md, color: VG.textSub, maxWidth: 380, lineHeight: 1.6 }}>
              The AI Agent needs a brand to work with.
            </p>
            <button
              onClick={() => navigate('/brand-dna/new')}
              style={{
                background: VG.gradient, color: '#fff', border: 'none',
                borderRadius: 12, padding: `${spacing.sm}px ${spacing.xl}px`,
                fontSize: typography.fontSize.md, fontWeight: 600,
                cursor: 'pointer', boxShadow: '0 4px 20px rgba(255,126,103,0.35)',
                fontFamily: 'inherit',
              }}
            >Create Brand</button>
          </div>
        )}

        {/* Empty state with quick-start suggestions */}
        {!threadLoading && !hasNoBrands && messages.length === 0 && !isStreaming && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 16, textAlign: 'center', padding: '0 20px' }}>
            <div style={{
              width: 48, height: 48, borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(255,126,103,0.12), rgba(255,77,141,0.12))',
              border: '1px solid rgba(255,126,103,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4,
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FF7E67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF' }}>What would you like to create?</div>
            <div style={{ fontSize: 13, color: VG.textMuted, maxWidth: 340, lineHeight: 1.6 }}>
              Describe what you need or pick a quick start below
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', maxWidth: 440 }}>
              {[
                { label: 'Product hero shot', prompt: 'Create a hero product shot of my main product. Clean studio lighting, minimal background, sharp focus.' },
                { label: 'Instagram post', prompt: 'Create an engaging Instagram feed post for my brand. 4:5 aspect ratio, bold visuals, minimal text.' },
                { label: 'Sale promotion', prompt: 'Create a bold sale graphic announcing 30% off. High energy, brand colors, clear CTA.' },
                { label: 'Brand awareness', prompt: 'Create a brand awareness lifestyle image that tells our brand story. Aspirational, emotional.' },
              ].map((s) => (
                <button
                  key={s.label}
                  onClick={() => { setInputText(s.prompt); }}
                  className="preset-card"
                  style={{
                    padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 500,
                    color: 'rgba(255,255,255,0.6)', textAlign: 'left',
                  }}
                >
                  {s.label}
                </button>
              ))}
              <button
                onClick={() => setPresetsOpen(true)}
                style={{
                  padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600,
                  background: 'rgba(255,126,103,0.1)', border: '1px solid rgba(255,126,103,0.25)',
                  color: VG.coral, cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Browse all templates &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg, idx) => {
          const isLastMsg = idx === messages.length - 1;
          const isLastAssistant = msg.role === 'assistant' && isLastMsg;
          return (
            <div key={msg.id} style={{ animation: 'messageIn 0.25s ease forwards' }}>
              <ChatMessage
                message={msg}
                completedImages={completedImages}
                userImageUrls={messageImages[msg.id]}
                isStreaming={isLastAssistant && isStreaming}
                devMode={devMode}
                onUseAsInput={handleUseAsInput}
                onImageAction={handleImageAction}
                onQuickReply={isLastAssistant && !isStreaming ? handleSend : undefined}
                brandId={activeThread?.metadata?.brand_id || activeThread?.brand_id || selectedBrandId}
                isLastMessage={isLastMsg}
                openAssetPicker={openAssetPicker}
              />
            </div>
          );
        })}

        {/* Upload progress indicator */}
        {uploadingCount > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
            animation: 'messageIn 0.2s ease forwards',
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'rgba(255,126,103,0.08)', border: '1px solid rgba(255,126,103,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,126,103,0.3)', borderTopColor: '#FF7E67', animation: 'spin 0.8s linear infinite' }} />
            </div>
            <div style={{
              padding: '8px 14px', borderRadius: '18px 18px 18px 4px',
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
              fontSize: 13, color: 'rgba(255,255,255,0.4)',
            }}>
              Uploading {uploadingCount} image{uploadingCount > 1 ? 's' : ''}...
            </div>
          </div>
        )}

        {/* Auto-analysis results card */}
        {analysisResults?.status === 'complete' && analysisResults.results?.length > 0 && (
          <AnalysisResultCard results={analysisResults.results} onDismiss={() => setAnalysisResults(null)} />
        )}

        <div ref={messagesEndRef} />

        {/* Scroll to bottom — sticky to bottom of scrollable area */}
        {showScrollButton && (
          <button
            onClick={() => {
              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              setShowScrollButton(false);
            }}
            aria-label="Scroll to bottom"
            style={{
              position: 'sticky', bottom: 12, alignSelf: 'center',
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.15)',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.6)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              animation: 'uroModalFadeIn 150ms ease',
              zIndex: 2, marginTop: -48, flexShrink: 0,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </div>

      {threadInfoOpen && threadId && (
        <ThreadInfoModal threadId={threadId} onClose={() => setThreadInfoOpen(false)} />
      )}

      {chatNetworkError && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 16px', flexShrink: 0,
          background: 'rgba(239,68,68,0.08)',
          borderTop: '1px solid rgba(239,68,68,0.2)',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span style={{ fontSize: 12, color: '#f87171', flex: 1, lineHeight: 1.4 }}>{chatNetworkError}</span>
          <button
            onClick={() => {
              setChatNetworkError(null);
              // Retry: resend the last user message
              const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
              if (lastUserMsg) {
                const text = typeof lastUserMsg.content === 'string'
                  ? lastUserMsg.content
                  : lastUserMsg.parts?.filter(p => p.type === 'text').map(p => p.text).join('') || '';
                if (text.trim()) handleSend(text);
              }
            }}
            style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, color: '#f87171', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 10px', flexShrink: 0, fontFamily: 'inherit' }}
          >Retry</button>
          <button
            onClick={() => setChatNetworkError(null)}
            style={{ background: 'none', border: 'none', color: 'rgba(248,113,113,0.6)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', flexShrink: 0 }}
          >✕</button>
        </div>
      )}

      {!hasNoBrands && (
        <ChatInput
          input={inputText}
          onInputChange={(e) => setInputText(e.target.value)}
          onSubmit={(e) => { e.preventDefault(); handleSend(inputText); }}
          onStop={() => { stopChat(); wasInterruptedRef.current = true; }}
          onQueueMessage={(text) => { setQueuedMessage(prev => prev ? `${prev}\n\n${text}` : text); setInputText(''); }}
          isStreaming={isStreaming}
          disabled={!selectedBrandId}
          images={attachedImages}
          onImagesChange={setAttachedImages}
          openAssetPicker={openAssetPicker}
          onOpenPresets={() => setPresetsOpen(true)}
          isMobile={isMobile}
          queuedMessage={queuedMessage}
          onClearQueue={() => setQueuedMessage(null)}
        />
      )}

      {/* Single shared AssetPickerModal instance */}
      <AssetPickerModal
        brandId={activeThread?.metadata?.brand_id || activeThread?.brand_id || selectedBrandId}
        open={assetPicker.open}
        onClose={closeAssetPicker}
        onConfirm={(assets) => {
          assetPicker.onConfirm?.(assets);
          closeAssetPicker();
        }}
      />

      {/* Preset prompts modal */}
      <PresetPromptsModal
        open={presetsOpen}
        onClose={() => setPresetsOpen(false)}
        onSelect={(prompt) => setInputText(prompt)}
      />
    </div>
  );
}
